/**
 * StudentTable - Table of students in a class
 *
 * Displays students with sheet numbers, names, and actions.
 */

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pencil, Trash2, Users, Plus, FileUp } from "lucide-react";
import type { LocalStudent } from "@/types/local-db.types";

interface StudentTableProps {
  students: LocalStudent[];
  onAddStudent: () => void;
  onEditStudent: (student: LocalStudent) => void;
  onDeleteStudent: (student: LocalStudent) => void;
  onImport: () => void;
  className?: string;
}

export function StudentTable({
  students,
  onAddStudent,
  onEditStudent,
  onDeleteStudent,
  onImport,
  className,
}: StudentTableProps) {
  if (students.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Students</h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onImport}>
              <FileUp className="mr-2 h-4 w-4" />
              Import CSV
            </Button>
            <Button size="sm" onClick={onAddStudent}>
              <Plus className="mr-2 h-4 w-4" />
              Add Student
            </Button>
          </div>
        </div>

        <div className="border rounded-lg p-8 text-center">
          <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground mb-4">
            No students in this class yet.
          </p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" size="sm" onClick={onImport}>
              <FileUp className="mr-2 h-4 w-4" />
              Import from CSV
            </Button>
            <Button size="sm" onClick={onAddStudent}>
              <Plus className="mr-2 h-4 w-4" />
              Add Student
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">
          Students ({students.length})
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onImport}>
            <FileUp className="mr-2 h-4 w-4" />
            Import CSV
          </Button>
          <Button size="sm" onClick={onAddStudent}>
            <Plus className="mr-2 h-4 w-4" />
            Add Student
          </Button>
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Sheet #</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((student) => (
              <TableRow key={student.id}>
                <TableCell className="font-mono">
                  {student.sheetNumber}
                </TableCell>
                <TableCell className="font-medium">
                  {student.name}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEditStudent(student)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onDeleteStudent(student)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
